import React from 'react';
import { useNavigate } from 'react-router-dom';
import { replacePathParams } from '@utils/replacePathWithParams';
import { routesPath } from '@router/routes';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { FileDxaIcon } from '@dynamika-shared/ui/icons';

type QctButtonProps = {
  clinicalTrialId?: string;
  taskId?: string;
  isQctModalityAssociated: boolean;
  /** The study's labels are being (re)fetched, so the answer is not settled. */
  isLoading?: boolean;
};

/**
 * Entry point to the QCT QC module (D7-3513).
 *
 * Plain terms
 * -----------
 * The button only appears for a study that actually uses QCT, the same way
 * the DXA button only appears for a DXA study. A study without QCT shows no
 * QCT button at all.
 *
 * Technical
 * ---------
 * Rendered only when the study has the QCT modality associated — mirrors the
 * sibling `DxaButton`, which returns `null` when its labels are absent.
 * "Associated with the study" is read from the clinical-trial context's
 * study-level labels, not from the current visit's labels, so a QCT study whose
 * visit has no QCT label still offers the module. `groupedLabels` starts as
 * `{}` in `clinicalTrialContext`, so the button stays hidden until
 * `fetchClinicalTrialContext` resolves, and is disabled during a refetch.
 */
export const QctButton = ({
  clinicalTrialId,
  taskId,
  isQctModalityAssociated,
  isLoading = false,
}: QctButtonProps) => {
  const navigate = useNavigate();

  if (!clinicalTrialId || !taskId || !isQctModalityAssociated) {
    return null;
  }

  // The route carries the QC task, which is what scopes the module to the
  // current subject and visit — the module never resolves a subject itself.
  const openQctModule = () =>
    navigate(
      replacePathParams(routesPath.qcTaskQCT, { id: clinicalTrialId, taskId })
    );

  return (
    <Button
      data-testid='QCTQualityControlButton'
      type='primary'
      disabled={isLoading}
      onClick={openQctModule}
      className='min-w-[12rem] rounded-lg ml-1 text-1624 font-semibold !bg-vividTangelo !text-white hover:!bg-dodgerBlue hover:!text-white'
    >
      <FileDxaIcon className='mr-[.5rem]' />
      QCT QC
    </Button>
  );
};
